import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CheckCircle, ArrowRight, Shield, Clock, Users, Send } from 'lucide-react';
import { motion } from 'framer-motion';
interface AboutPageProps {
  onOpenSampleModal?: () => void;
}
const sectors = ['Defence & Aerospace', 'Electronics & Instrumentation', 'Pumps, Valves & Flow control', 'Medical Equipment & NHS Organisations', 'Test & Calibration', 'Catering, Refrigeration and Air Conditioning and Dust Control', 'Automotive, Rail and Marine', 'Playground Equipment', 'Agriculture, Garden Machinery and Farm Equipment', 'Steam and Transport Rallies, Classic Car and Bike Clubs and Events'];
const certifications = [{
  src: 'https://cdn.magicpatterns.com/uploads/dNexntwFHMkm7ZPkJVXEbM/iso-9001-certified.jpg',
  alt: 'ISO 9001',
  label: 'ISO 9001'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gpDeLGDsven44JSBqjzPBx/ISO_14001.jpg',
  alt: 'ISO 14001',
  label: 'ISO 14001'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/osdWtkSKVjaRAt7iCy6kEa/Joscar.png',
  alt: 'JOSCAR',
  label: 'JOSCAR'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6FuydEZynCq18cGM9hgcSz/Cyber.jpg',
  alt: 'Cyber Essentials',
  label: 'Cyber Essentials'
}];
export function AboutPage({
  onOpenSampleModal
}: AboutPageProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-532">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-533" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-534">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-535">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-536">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-537">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-538">
                <span className="text-xs font-bold text-[#0052CC] uppercase tracking-widest mb-4 block" data-id="element-539">
                  About Us
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0A1628] tracking-tight leading-[1.08] mb-6" data-id="element-540">
                  Industrial Labels & Nameplates
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed mb-6" data-id="element-541">
                  One of the UK's leading self-adhesive label and metal
                  nameplate manufacturers.
                </p>
                <p className="text-lg text-slate-500 leading-relaxed" data-id="element-542">
                  We specialise in industrial applications and we have a "Back
                  to Basics" approach with our emphasis on customer requirements
                  backed up by our industry expertise from both our management
                  and production teams.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="relative" data-id="element-543">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 shadow-2xl" data-id="element-544">
                  <img src="https://cdn.magicpatterns.com/uploads/qszDzSmxSCirrNe3REUbpj/nameplates-industial.webp" alt="Industrial nameplates manufacturing" className="w-full h-full object-cover" data-id="element-545" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-16 md:py-24 bg-slate-50" data-id="element-546">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-547">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="max-w-4xl mb-16" data-id="element-548">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-8" data-id="element-549">
                Our Products
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-550">
                Our products include identification labels, inventory labels,
                labels in rolls, labels in sheets, labels in sets, labels in
                kits, screen printed labels, digitally printed labels, hot foil
                printed labels, flexographic printed labels, bar-coded labels,
                sign and seal labels in a variety of plastics including
                polyester, PVC, polyimide, polycarbonate, and vinyl.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed" data-id="element-551">
                We manufacture metal nameplates in anodised aluminium, screen
                printed aluminium, etched brass and stainless steel engraving
                laminates and we can mark parts if required.
              </p>
            </motion.div>

            {/* Product Image Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-552">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0
            }} className="rounded-2xl overflow-hidden bg-slate-100 shadow-md aspect-[4/3]" data-id="element-553">
                <img src="https://cdn.magicpatterns.com/uploads/4GroWq4twSqQJRdxL7S1mT/sample_3.webp" alt="Industrial nameplates samples" className="w-full h-full object-cover" data-id="element-554" />
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="rounded-2xl overflow-hidden bg-slate-100 shadow-md aspect-[4/3]" data-id="element-555">
                <img src="https://cdn.magicpatterns.com/uploads/dympUc8nfN6AgL4ARo7EBA/sample2.webp" alt="Asset labels samples" className="w-full h-full object-cover" data-id="element-556" />
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="rounded-2xl overflow-hidden bg-slate-100 shadow-md aspect-[4/3]" data-id="element-557">
                <img src="https://cdn.magicpatterns.com/uploads/tUEr7Zfb8BdMnYMRBFx6SN/sample.png" alt="Machine sign labels" className="w-full h-full object-cover" data-id="element-558" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Sectors Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-559">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-560">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-561">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-562">
                Industries We Serve
              </h2>
              <p className="text-lg text-slate-500 mb-12 max-w-2xl" data-id="element-563">
                Our experience and expertise spans a wide number of
                manufacturing sectors:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-564">
                {sectors.map((sector, index) => <motion.div key={sector} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.05
              }} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-id="element-565">
                    <CheckCircle className="h-5 w-5 text-[#0052CC] flex-shrink-0 mt-0.5" data-id="element-566" />
                    <span className="text-slate-700" data-id="element-567">{sector}</span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Difference Section */}
        <section className="py-16 md:py-24 bg-[#0A1628]" data-id="element-568">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-569">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center max-w-3xl mx-auto" data-id="element-570">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-8" data-id="element-571">
                What Makes Us Different
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-572">
                At Industrial Labels & Nameplates we pride ourselves in being
                that bit different to all of our competition.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed mb-12" data-id="element-573">
                We have state of the art manufacturing technology but it is our
                total commitment to our "Right First Time on Time" policy that
                makes Industrial Labels & Nameplates stand out from the crowd.
                We offer our customers clearly explained solutions without
                industry jargon to provide the very best products at competitive
                prices with on time delivery.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-574">
                <div className="bg-white/10 rounded-2xl p-6 text-center" data-id="element-575">
                  <div className="h-12 w-12 bg-[#0052CC] rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-576">
                    <Shield className="h-6 w-6 text-white" data-id="element-577" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-578">
                    Quality Assured
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-579">
                    Right First Time on Time policy
                  </p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 text-center" data-id="element-580">
                  <div className="h-12 w-12 bg-[#0052CC] rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-581">
                    <Clock className="h-6 w-6 text-white" data-id="element-582" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-583">
                    On Time Delivery
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-584">
                    Competitive prices with reliable delivery
                  </p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6 text-center" data-id="element-585">
                  <div className="h-12 w-12 bg-[#0052CC] rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-586">
                    <Users className="h-6 w-6 text-white" data-id="element-587" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-588">
                    Expert Support
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-589">
                    Clear solutions without industry jargon
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-590">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-591">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center" data-id="element-592">
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-593">
                Our Certifications
              </h2>
              <p className="text-lg text-slate-500 mb-12 max-w-2xl mx-auto" data-id="element-594">
                We maintain the highest standards of quality and compliance
                across all our operations.
              </p>

              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12" data-id="element-595">
                {certifications.map((cert, index) => <motion.div key={index} initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center gap-3" data-id="element-596">
                    <div className="h-20 w-28 bg-white rounded-xl flex items-center justify-center p-3 border border-slate-200 shadow-sm" data-id="element-597">
                      <img src={cert.src} alt={cert.alt} className="max-w-full max-h-full object-contain" data-id="element-598" />
                    </div>
                    <span className="text-sm font-medium text-slate-600" data-id="element-599">
                      {cert.label}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Enquiry Form Section */}
        <section className="py-16 md:py-24 bg-slate-50" data-id="element-600">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-601">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-602">
              <div className="max-w-2xl mx-auto" data-id="element-603">
                <div className="text-center mb-12" data-id="element-604">
                  <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-605">
                    Send Us an Enquiry
                  </h2>
                  <p className="text-lg text-slate-500" data-id="element-606">
                    Have a question or need a quote? Fill out the form below and
                    we'll get back to you promptly.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-607">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-608">
                    <div className="space-y-2" data-id="element-609">
                      <label htmlFor="name" className="block text-sm font-semibold text-[#0A1628]" data-id="element-610">
                        Full Name
                      </label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="e.g. John Smith" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all" data-id="element-611" />
                    </div>
                    <div className="space-y-2" data-id="element-612">
                      <label htmlFor="email" className="block text-sm font-semibold text-[#0A1628]" data-id="element-613">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="john@company.com" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all" data-id="element-614" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-615">
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#0A1628]" data-id="element-616">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+44 123 456 7890" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all" data-id="element-617" />
                  </div>

                  <div className="space-y-2" data-id="element-618">
                    <label htmlFor="message" className="block text-sm font-semibold text-[#0A1628]" data-id="element-619">
                      Your Message
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required rows={5} placeholder="Tell us about your requirements..." className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all resize-none" data-id="element-620" />
                  </div>

                  <button type="submit" className="w-full py-4 bg-[#0052CC] text-white font-bold rounded-full hover:bg-[#0040A0] transition-all duration-200 flex items-center justify-center shadow-lg shadow-[#0052CC]/20 hover:shadow-xl hover:shadow-[#0052CC]/30 hover:-translate-y-0.5" data-id="element-621">
                    Send Enquiry
                    <Send className="ml-2 h-5 w-5" data-id="element-622" />
                  </button>

                  <p className="text-xs text-center text-slate-400" data-id="element-623">
                    We'll respond to your enquiry within 24 hours.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-624" />
    </div>;
}